/**
 * Pure math for HR-4624: pausing a group session only freezes the timers of the client tab that is
 * currently mounted. The other clients' `data` documents are never touched while their tab is
 * unmounted, so their timers keep running by wall clock (elapsed = accumulated + (now - offset)).
 * This module computes the same freeze/restore a mounted timer component already does to itself on
 * pause/resume, so it can be applied headlessly to the sessions nobody has open. `sessions-db.js`
 * does the Firestore reads/writes; this file never imports firebase and works entirely in millis so
 * it stays testable without mocking Firestore.
 *
 * Every patch below is `{ accumulate, offsets } | null`: `accumulate` holds plain numbers/flags to
 * write as-is, `offsets` holds epoch millis (or null) that the caller must convert to a Firestore
 * Timestamp. The split is structural rather than by field name, since the per-target field names
 * are dynamic.
 */

// Owned by components/Programs/DataCollection/IntervalTarget.vue - if it renames these fields,
// freezeDataDocPatch()/restoreDataDocPatch() silently stop finding this family's timers, so the
// HR-4624 pause bug comes back for interval targets with no test failure. Kept honest by the
// "keeps in sync with" guard test in sessionPauseTimers.test.js.
const INTERVAL_TARGET_PREFIXES = ['duration_', 'timerOffsetTimestamp_'];
// Owned by DurationData.vue / RateData.vue - same risk and same guard test as above.
const DURATION_TARGET_PREFIXES = ['timerDuration_', 'timerOffset_'];

/**
 * @param {*} value Firestore Timestamp | {toMillis()} | {seconds, nanoseconds} | Date | number
 * @return {number | null}
 */
export function toMillis(value) {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value.seconds === 'number') {
    return (value.seconds * 1000) + Math.round((value.nanoseconds || 0) / 1e6);
  }
  return null;
}

/** Ids found in a `data` document's own keys for a given family of per-target fields. */
function extractTargetIds(dataDoc, prefixes) {
  const ids = new Set();
  Object.keys(dataDoc).forEach((key) => {
    const prefix = prefixes.find((candidate) => key.startsWith(candidate));
    if (prefix) {
      ids.add(key.slice(prefix.length));
    }
  });
  return [...ids];
}

/**
 * Sessions in the group whose timers this device must pause alongside the active one: same owner,
 * not the tab being paused, and neither already paused nor already frozen by us.
 * @param {Array<Object>} groupSessions
 * @param {{ activeSessionId: string, userId: string }} params
 * @return {Array<Object>}
 */
export function selectSessionsToPause(groupSessions, { activeSessionId, userId }) {
  if (!userId || !Array.isArray(groupSessions)) {
    return [];
  }
  return groupSessions.filter((session) => session?.id
    && session.id !== activeSessionId
    && session.runBy === userId
    && !session.durationIsPaused
    && session.sessionPauseFrozenAt == null);
}

/**
 * Sessions carrying this device's freeze marker, which must now be thawed. Selecting on the marker
 * rather than on `durationIsPaused` keeps a pause somebody else started from being lifted here, and
 * the active session is deliberately not excluded: it may have been frozen while another client was
 * on screen, and an app restart makes a different session of the group the active one.
 * @param {Array<Object>} groupSessions
 * @param {{ userId: string }} params
 * @return {Array<Object>}
 */
export function selectSessionsToResume(groupSessions, { userId }) {
  if (!userId || !Array.isArray(groupSessions)) {
    return [];
  }
  return groupSessions.filter((session) => session?.id
    && session.runBy === userId
    && session.sessionPauseFrozenAt != null);
}

/**
 * Which of a session's offset fields a freeze has to rebase on resume. CollectData.vue writes the
 * active session's marker itself rather than through freezeSessionPatch(), so the rule lives here
 * and both callers stay in step.
 * @param {Object} session A `sessions/{id}` document
 * @return {Array<string>} empty for a session whose timer was already stopped, so
 *   restoreSessionPatch() leaves it stopped
 */
export function frozenSessionOffsetFields(session) {
  return toMillis(session?.durationOffsetTimestamp) == null ? [] : ['durationOffsetTimestamp'];
}

/**
 * @param {Object} session A `sessions/{id}` document
 * @param {number} pausedAtMs
 * @return {{accumulate: Object, offsets: Object} | null}
 */
export function freezeSessionPatch(session, pausedAtMs) {
  if (!session) {
    return null;
  }

  const frozenFields = frozenSessionOffsetFields(session);
  const accumulate = { durationIsPaused: true, sessionPauseFrozenOffsets: frozenFields };
  const offsets = { sessionPauseFrozenAt: pausedAtMs };

  if (frozenFields.length) {
    const offsetMs = toMillis(session.durationOffsetTimestamp);
    accumulate.duration = (session.duration || 0) + Math.floor((pausedAtMs - offsetMs) / 1000);
    offsets.durationOffsetTimestamp = null;
  }

  return { accumulate, offsets };
}

/**
 * Lifts a freeze written by {@link freezeSessionPatch}, rebasing only the offset it actually froze -
 * a session whose timer was already stopped before the pause must stay stopped.
 * @param {Object} session A `sessions/{id}` document
 * @param {number} resumedAtMs
 * @return {{accumulate: Object, offsets: Object} | null} null when this session was never frozen
 */
export function restoreSessionPatch(session, resumedAtMs) {
  if (!session || session.sessionPauseFrozenAt == null) {
    return null;
  }

  const offsets = { sessionPauseFrozenAt: null };
  (session.sessionPauseFrozenOffsets || []).forEach((field) => {
    offsets[field] = resumedAtMs;
  });

  return {
    accumulate: { durationIsPaused: false, sessionPauseFrozenOffsets: null },
    offsets,
  };
}

/**
 * The active session's own timer fields belong to CollectData.vue's resumeSessionTimer() and to the
 * mounted components; when it turns out to carry a freeze marker (it was frozen while another
 * client was on screen), only the marker itself is ours to clear.
 * @param {Object} session A `sessions/{id}` document
 * @return {{accumulate: Object, offsets: Object} | null} null when this session was never frozen
 */
export function clearSessionFreezePatch(session) {
  if (!session || session.sessionPauseFrozenAt == null) {
    return null;
  }

  return {
    accumulate: { sessionPauseFrozenOffsets: null },
    offsets: { sessionPauseFrozenAt: null },
  };
}

/**
 * Freezes every live timer recorded on one `data` document (program-level frequency/interval/esdm,
 * per-target interval, per-target duration/rate) by folding the elapsed time into their accumulator
 * and clearing the offset - a mounted component elsewhere (e.g. a second device with this session
 * open) that reacts to the new accumulator must not also recompute elapsed time from a now-stale
 * offset. Which offset fields were live is recorded in `sessionPauseFrozenOffsets` so
 * restoreDataDocPatch() knows exactly what to restore. Guarded by `sessionPauseFrozenAt` so calling
 * this twice before a restore cannot double-count.
 * @param {Object} dataDoc A `data/{id}` document
 * @param {number} pausedAtMs
 * @return {{accumulate: Object, offsets: Object} | null} null when nothing on this document is running
 */
export function freezeDataDocPatch(dataDoc, pausedAtMs) {
  if (!dataDoc || dataDoc.sessionPauseFrozenAt != null) {
    return null;
  }

  const accumulate = {};
  const offsets = {};
  const frozenFields = [];

  const programOffsetMs = toMillis(dataDoc.timerOffsetTimestamp);
  if (programOffsetMs != null) {
    accumulate.duration = (dataDoc.duration ?? 0) + ((pausedAtMs - programOffsetMs) / 1000);
    offsets.timerOffsetTimestamp = null;
    frozenFields.push('timerOffsetTimestamp');
  }

  // Only a target with a live offset of its own is frozen: IntervalTarget.vue writes duration_<T>
  // and timerOffsetTimestamp_<T> as a pair, so own fields with an empty own offset means that
  // target's timer is stopped, and folding the program-level elapsed time into it would re-create
  // HR-4624 on the very target this fixes. A target with no own fields never reaches here, and the
  // program-level freeze above already covers it. `??`, not `||`: an own accumulator of 0 is real.
  extractTargetIds(dataDoc, INTERVAL_TARGET_PREFIXES).forEach((targetId) => {
    const offsetField = `timerOffsetTimestamp_${targetId}`;
    const offsetMs = toMillis(dataDoc[offsetField]);
    if (offsetMs == null) {
      return;
    }
    const baseDuration = dataDoc[`duration_${targetId}`] ?? 0;
    accumulate[`duration_${targetId}`] = baseDuration + ((pausedAtMs - offsetMs) / 1000);
    offsets[offsetField] = null;
    frozenFields.push(offsetField);
  });

  // Duration/rate targets have no program-level fallback — each writes its own pair of fields.
  extractTargetIds(dataDoc, DURATION_TARGET_PREFIXES).forEach((targetId) => {
    const offsetField = `timerOffset_${targetId}`;
    const offsetMs = toMillis(dataDoc[offsetField]);
    if (offsetMs == null) {
      return;
    }
    const baseDuration = dataDoc[`timerDuration_${targetId}`] ?? 0;
    accumulate[`timerDuration_${targetId}`] = baseDuration + ((pausedAtMs - offsetMs) / 1000);
    offsets[offsetField] = null;
    frozenFields.push(offsetField);
  });

  if (frozenFields.length === 0) {
    return null;
  }

  accumulate.sessionPauseFrozenOffsets = frozenFields;
  offsets.sessionPauseFrozenAt = pausedAtMs;
  return { accumulate, offsets };
}

/**
 * Rebases every offset this module froze back to `resumedAtMs`, mirroring what a mounted
 * `sessionPaused` watcher does on resume: the accumulator already holds everything up to the pause,
 * so the offset only needs to move to "now", not shift by the pause length. Which fields to rebase
 * comes from `sessionPauseFrozenOffsets`, not from re-inspecting the document - freezeDataDocPatch()
 * clears those fields, so their current value can no longer say which ones were live.
 * @param {Object} dataDoc A `data/{id}` document
 * @param {number} resumedAtMs
 * @return {{accumulate: Object, offsets: Object} | null} null when this document was never frozen
 */
export function restoreDataDocPatch(dataDoc, resumedAtMs) {
  if (!dataDoc || dataDoc.sessionPauseFrozenAt == null) {
    return null;
  }

  const offsets = { sessionPauseFrozenAt: null };
  (dataDoc.sessionPauseFrozenOffsets || []).forEach((field) => {
    offsets[field] = resumedAtMs;
  });

  return { accumulate: { sessionPauseFrozenOffsets: null }, offsets };
}
